import { Component, useEffect, useRef, useState, type ReactNode } from 'react';
import { useApp } from '../lib/app-context';
import { Icon, type IconName } from './Icon';
import { reportCrash } from '../lib/crashReport';

/*
 * The app's error screens, one look for all of them: a page that does not exist (404), a page that broke
 * (500), the service out of reach or being updated (503, retried by itself), and no internet connection.
 * Nothing technical is shown to people; they get a way forward.
 */

export type ErrorKind = 'notFound' | 'crashed' | 'unavailable' | 'offline';

const LOOK: Record<ErrorKind, { icon: IconName; code: string }> = {
  notFound: { icon: 'compass', code: '404' },
  crashed: { icon: 'alert', code: '500' },
  unavailable: { icon: 'clock', code: '503' },
  offline: { icon: 'wifiOff', code: '' },
};

/** Seconds between automatic retries while the service is out of reach. */
const RETRY_SECONDS = 15;

export function ErrorScreen({ kind, onRetry, page = false }: { kind: ErrorKind; onRetry?: () => void; page?: boolean }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const look = LOOK[kind];
  const retry = onRetry ?? (() => location.reload());
  const retryRef = useRef(retry);
  useEffect(() => {
    retryRef.current = retry;
  });
  const [left, setLeft] = useState(RETRY_SECONDS);

  // Out of reach or offline: try again by itself every few seconds, and at once when the connection is back.
  const waits = kind === 'unavailable' || kind === 'offline';
  useEffect(() => {
    if (!waits) return;
    let remaining = RETRY_SECONDS;
    const timer = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        remaining = RETRY_SECONDS;
        if (navigator.onLine) retryRef.current();
      }
      setLeft(remaining);
    }, 1000);
    const back = () => retryRef.current();
    window.addEventListener('online', back);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', back);
    };
  }, [waits]);

  return (
    <div className={`error-screen${page ? ' is-page' : ''}`} role="alert">
      <div className="error-screen-card">
        <span className="error-screen-icon" aria-hidden>
          <Icon name={look.icon} size={34} />
        </span>
        {look.code && <span className="error-screen-code">{look.code}</span>}
        <h1>{t(`errorScreen.${kind}.title`)}</h1>
        <p className="muted">{t(`errorScreen.${kind}.text`)}</p>
        {waits && <p className="hint">{t('errorScreen.retrying', { seconds: left })}</p>}
        <div className="error-screen-actions">
          {kind === 'notFound' ? (
            <>
              <a className="btn btn-primary" href="/">
                <Icon name="home" size={16} /> {t('errorScreen.home')}
              </a>
              {history.length > 1 && (
                <button type="button" className="btn" onClick={() => history.back()}>
                  {t('errorScreen.back')}
                </button>
              )}
            </>
          ) : (
            <>
              <button type="button" className="btn btn-primary" onClick={retry}>
                {t('common.retry')}
              </button>
              {kind === 'crashed' && (
                <>
                  <a className="btn" href="/">
                    {t('errorScreen.home')}
                  </a>
                  <a className="btn" href={`/feedback?category=bug&from=${encodeURIComponent(location.pathname)}`}>
                    {t('errorScreen.report')}
                  </a>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** A page address that does not exist inside the app. */
export function NotFoundPage() {
  return <ErrorScreen kind="notFound" />;
}

/**
 * Last line of defence around the whole app: if even the app's own frame breaks, a plain screen in the
 * browser's language (no app state is used here, since that may be what broke).
 */
export class RootBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(err: unknown) {
    reportCrash(err);
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    const tr = (navigator.language || 'tr').toLowerCase().startsWith('tr');
    return (
      <div className="error-screen is-page" role="alert">
        <div className="error-screen-card">
          <span className="error-screen-code">500</span>
          <h1>{tr ? 'Bir şeyler ters gitti' : 'Something went wrong'}</h1>
          <p className="muted">{tr ? 'Uygulama beklenmedik bir hatayla karşılaştı. Sayfayı yenilemek genellikle sorunu giderir.' : 'The app ran into an unexpected error. Reloading the page usually fixes it.'}</p>
          <div className="error-screen-actions">
            <button type="button" className="btn btn-primary" onClick={() => location.reload()}>
              {tr ? 'Sayfayı yenile' : 'Reload the page'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
